/**
 * The client for an operator's OWN account/credential changes (controlplane-auth task 13.1;
 * requirements A5.3). The ProfilePage uses these to drive the password-reset and TOTP-MFA
 * affordances; they are the UI half of the Phase-2 credential handlers (`login.ts`, `email-otp.ts`).
 *
 * These are TOP-LEVEL `/auth/*` routes (not `/api/v1`), so this builds on {@link authFetch}
 * (`lib/auth-client.ts`) — same origin, the cookie-borne session, the shared {@link ApiError}
 * classes. The acting operator is identified by the SESSION on the server; this client sends no user
 * id (the server never trusts a client-supplied subject — Property 3).
 *
 * ENDPOINTS (canonical paths the Phase-2 worker handlers back):
 *   - `POST /auth/forgot {email}`  — begin a password reset (always a no-enumeration 200). The
 *     operator then completes it with the emailed code on the login screen's reset step. The profile
 *     "change password" affordance triggers this self-service flow for the signed-in operator's own
 *     email, so a password change always flows through the verified-email reset primitive rather
 *     than a direct set (no plaintext ever posted here).
 *   - `POST /auth/mfa/enrol`       — begin TOTP enrolment → returns the secret (base32 + otpauth URI)
 *     to show as a QR/text.
 *   - `POST /auth/mfa/confirm {secretRef, code}` — confirm enrolment with a code from the auth-
 *     enticator; returns one-time recovery codes to show once.
 *   - `POST /auth/mfa/unenrol`     — disable TOTP MFA for the signed-in operator.
 *
 * BACKEND-ROUTING GAP (Phase 7 / task 15): as documented in task 12, the Phase-2 handlers exist in
 * the worker but only `/auth/switch-team` is routed in `index.ts` today; the rest are wired at the
 * cutover. The ProfilePage posts to these correct paths now (matching task 12's approach); until the
 * cutover an unrouted path falls through to the asset router and surfaces here as a `malformed`
 * ApiError, which the screen shows as a friendly "try again" rather than crashing.
 */

import { authFetch } from "./auth-client.ts";
import { ApiError } from "./api.ts";
import { startRegistration, startAuthentication } from "@simplewebauthn/browser";
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";

/** The success shape of an `/auth/*` envelope (`{ status, message, data }`). */
interface AuthEnvelope<T> {
  readonly status?: boolean;
  readonly message?: string;
  readonly data?: T | null;
}

/**
 * Begin a self-service password change for the signed-in operator's own email. Mirrors the login
 * screen's "forgot" start: the server emails a single-use code; the operator completes the reset
 * with it. Always resolves (no enumeration) when the backend is routed.
 */
export async function startPasswordChange(email: string, signal?: AbortSignal): Promise<void> {
  await authFetch<AuthEnvelope<unknown>>("/auth/forgot", {
    method: "POST",
    body: { email },
    ...(signal === undefined ? {} : { signal }),
  });
}

/**
 * Complete the self-service password change with the single-use code emailed by
 * {@link startPasswordChange}. Posts `{email, code, newPassword}` to `/auth/reset`; on success the
 * server sets a fresh Argon2id hash and revokes the operator's existing sessions (defence in depth,
 * so the operator must sign in again with the new password). Throws {@link ApiError} on any failure
 * (e.g. `unauthorized` for a wrong/expired/used code).
 */
export async function completePasswordChange(
  email: string,
  code: string,
  newPassword: string,
  signal?: AbortSignal,
): Promise<void> {
  await authFetch<AuthEnvelope<unknown>>("/auth/reset", {
    method: "POST",
    body: { email, code, newPassword },
    ...(signal === undefined ? {} : { signal }),
  });
}

/** The challenge returned by beginning TOTP enrolment — shown to the operator as a QR/secret. */
export interface MfaEnrolChallenge {
  /** An opaque reference to the pending secret (passed back to confirm; the secret stays server-side). */
  readonly secretRef: string;
  /** The base32 secret the operator can type into an authenticator app. */
  readonly secret: string;
  /** The `otpauth://` URI for a QR code, when the server provides one. */
  readonly otpauthUri?: string;
}

/** Shape the enrol-begin `data` defensively; `null` when it lacks a usable secret reference. */
function parseEnrolChallenge(data: unknown): MfaEnrolChallenge | null {
  if (typeof data !== "object" || data === null) return null;
  const r = data as Record<string, unknown>;
  const secretRef = r["secret_ref"] ?? r["secretRef"];
  // The server returns the base32 secret as `secret_base32`; also accept `secret`/`base32` so a
  // future envelope shape still parses rather than failing closed to a "malformed" error.
  const secret = r["secret_base32"] ?? r["secretBase32"] ?? r["secret"] ?? r["base32"];
  if (typeof secretRef !== "string" || secretRef.length === 0) return null;
  if (typeof secret !== "string" || secret.length === 0) return null;
  const uri = r["otpauth_uri"] ?? r["otpauthUri"];
  return {
    secretRef,
    secret,
    ...(typeof uri === "string" ? { otpauthUri: uri } : {}),
  };
}

/** Begin TOTP MFA enrolment for the signed-in operator. */
export async function beginMfaEnrolment(signal?: AbortSignal): Promise<MfaEnrolChallenge> {
  const body = await authFetch<AuthEnvelope<unknown>>("/auth/mfa/enrol", {
    method: "POST",
    ...(signal === undefined ? {} : { signal }),
  });
  const challenge = parseEnrolChallenge(body?.data ?? null);
  if (challenge === null) {
    throw new ApiError("malformed", "The server did not return an enrollment challenge.");
  }
  return challenge;
}

/** Pull the recovery codes out of a confirm `data` payload (shown once). */
function parseRecoveryCodes(data: unknown): readonly string[] {
  if (typeof data !== "object" || data === null) return [];
  const codes = (data as { recovery_codes?: unknown; recoveryCodes?: unknown }).recovery_codes
    ?? (data as { recoveryCodes?: unknown }).recoveryCodes;
  if (!Array.isArray(codes)) return [];
  return codes.filter((c): c is string => typeof c === "string");
}

/**
 * Confirm TOTP enrolment with a code from the authenticator. Returns one-time recovery codes to show
 * the operator exactly once (empty when the server returns none).
 */
export async function confirmMfaEnrolment(
  secretRef: string,
  code: string,
  signal?: AbortSignal,
): Promise<readonly string[]> {
  const body = await authFetch<AuthEnvelope<unknown>>("/auth/mfa/confirm", {
    method: "POST",
    body: { secret_ref: secretRef, code },
    ...(signal === undefined ? {} : { signal }),
  });
  return parseRecoveryCodes(body?.data ?? null);
}

/** Disable TOTP MFA for the signed-in operator. */
export async function unenrolMfa(signal?: AbortSignal): Promise<void> {
  await authFetch<AuthEnvelope<unknown>>("/auth/mfa/unenrol", {
    method: "POST",
    ...(signal === undefined ? {} : { signal }),
  });
}


// -------------------------------------------------------------------------------------------------
// WebAuthn / FIDO2 passkeys (passwordless). The control-plane Worker is the Relying Party; these
// helpers run the browser half of each ceremony with the pinned @simplewebauthn/browser and post the
// authenticator's response back for server verification. The server derives the user from the
// session (register) or the matched credential (authenticate) — the client never sends a user id.

/** A registered passkey as the account UI lists it. */
export interface Passkey {
  readonly id: string;
  readonly label: string | null;
  readonly deviceType: string | null;
  readonly backedUp: boolean;
  readonly transports: readonly string[];
  readonly createdAt: number | null;
  readonly lastUsedAt: number | null;
}

/** True when the current browser can do WebAuthn at all (no passkey UI otherwise). */
export function isWebAuthnSupported(): boolean {
  return typeof window !== "undefined" && typeof window.PublicKeyCredential === "function";
}

/**
 * Register a new passkey for the signed-in operator. Begins the ceremony on the server (which mints
 * creation options + a single-use handle), runs `startRegistration` so the authenticator (native
 * platform, phone, security key — any FIDO2) creates the credential, then posts the response back to
 * verify + persist it. Throws an {@link ApiError} on a server failure; a user cancelling the native
 * prompt throws the browser's own error (the caller shows a friendly "cancelled" message).
 */
export async function registerPasskey(signal?: AbortSignal): Promise<void> {
  const begin = await authFetch<{ data?: { handle?: string; options?: PublicKeyCredentialCreationOptionsJSON } }>(
    "/auth/webauthn/register/options",
    { method: "POST", ...(signal === undefined ? {} : { signal }) },
  );
  const handle = begin?.data?.handle;
  const options = begin?.data?.options;
  if (typeof handle !== "string" || options === undefined) {
    throw new ApiError("malformed", "The server did not return a registration challenge.");
  }
  const response = await startRegistration({ optionsJSON: options });
  await authFetch<AuthEnvelope<unknown>>("/auth/webauthn/register/verify", {
    method: "POST",
    body: { handle, response },
    ...(signal === undefined ? {} : { signal }),
  });
}

/** Shape a raw passkey row from the server `data.credentials` defensively. */
function parsePasskey(raw: unknown): Passkey | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r["id"] !== "string") return null;
  const transports = Array.isArray(r["transports"])
    ? (r["transports"] as unknown[]).filter((t): t is string => typeof t === "string")
    : [];
  return {
    id: r["id"],
    label: typeof r["label"] === "string" ? r["label"] : null,
    deviceType: typeof r["device_type"] === "string" ? r["device_type"] : null,
    backedUp: r["backed_up"] === true,
    transports,
    createdAt: typeof r["created_at"] === "number" ? r["created_at"] : null,
    lastUsedAt: typeof r["last_used_at"] === "number" ? r["last_used_at"] : null,
  };
}

/** List the signed-in operator's registered passkeys. */
export async function listPasskeys(signal?: AbortSignal): Promise<readonly Passkey[]> {
  const body = await authFetch<AuthEnvelope<{ credentials?: unknown }>>("/auth/webauthn/credentials", {
    ...(signal === undefined ? {} : { signal }),
  });
  const rows = body?.data?.credentials;
  if (!Array.isArray(rows)) return [];
  const out: Passkey[] = [];
  for (const row of rows) {
    const p = parsePasskey(row);
    if (p !== null) out.push(p);
  }
  return out;
}

/** Remove one of the signed-in operator's passkeys by its credential id. */
export async function removePasskey(credentialId: string, signal?: AbortSignal): Promise<void> {
  await authFetch<AuthEnvelope<unknown>>("/auth/webauthn/credentials/delete", {
    method: "POST",
    body: { credential_id: credentialId },
    ...(signal === undefined ? {} : { signal }),
  });
}

/** Rename one of the signed-in operator's passkeys. An empty label clears it (back to a derived name). */
export async function renamePasskey(credentialId: string, label: string, signal?: AbortSignal): Promise<void> {
  await authFetch<AuthEnvelope<unknown>>("/auth/webauthn/credentials/rename", {
    method: "POST",
    body: { credential_id: credentialId, label },
    ...(signal === undefined ? {} : { signal }),
  });
}

/**
 * Sign in with a passkey (passwordless). Begins a pre-session authentication ceremony, runs
 * `startAuthentication` so the authenticator presents the user's discoverable credentials, and posts
 * the signed assertion back; on success the server issues the `oa_session` cookie exactly like a
 * password login. Throws an {@link ApiError} when the server rejects the assertion.
 */
export async function loginWithPasskey(signal?: AbortSignal): Promise<void> {
  const begin = await authFetch<{ data?: { handle?: string; options?: PublicKeyCredentialRequestOptionsJSON } }>(
    "/auth/webauthn/authenticate/options",
    { method: "POST", ...(signal === undefined ? {} : { signal }) },
  );
  const handle = begin?.data?.handle;
  const options = begin?.data?.options;
  if (typeof handle !== "string" || options === undefined) {
    throw new ApiError("malformed", "The server did not return a sign-in challenge.");
  }
  const response = await startAuthentication({ optionsJSON: options });
  await authFetch<AuthEnvelope<unknown>>("/auth/webauthn/authenticate/verify", {
    method: "POST",
    body: { handle, response },
    ...(signal === undefined ? {} : { signal }),
  });
}


// -------------------------------------------------------------------------------------------------
// Active second factor. TOTP and a passkey may both be enrolled, but only one is ENABLED at a time.

/** Which second factor is enabled (enforced on password login). */
export type ActiveSecondFactor = "none" | "totp" | "passkey" | "sso";

/** The known SSO provider ids (mirrors management-client). */
export type SsoProviderId = "entra" | "okta" | "1kosmos" | "google" | "other";

/** The team SSO details resolved for an email at the login page (user tab). */
export interface SsoForEmail {
  readonly tenantId: string;
  readonly orgName: string;
  readonly providerId: SsoProviderId;
  readonly providerLabel: string;
  readonly providerLogoUrl: string | null;
}

/**
 * Resolve the SSO provider for an email at the login page (`POST /auth/sso-lookup`). Returns the
 * team's org name + provider when the email belongs to a team with SSO configured, else null (an
 * unknown email or a team without SSO both return null — a generic, non-confirming answer). For a
 * built-in provider the canonical label + logo win over whatever is stored.
 */
export async function getSsoForEmail(email: string, signal?: AbortSignal): Promise<SsoForEmail | null> {
  const body = await authFetch<AuthEnvelope<{
    sso?: {
      tenant_id?: string;
      org_name?: string;
      provider_id?: string;
      provider_label?: string;
      provider_logo_url?: string | null;
    } | null;
  }>>("/auth/sso-lookup", {
    method: "POST",
    body: { email },
    ...(signal === undefined ? {} : { signal }),
  });
  const sso = body?.data?.sso;
  if (sso === undefined || sso === null || typeof sso.tenant_id !== "string") return null;
  const pid = (["entra", "okta", "1kosmos", "google", "other"] as const).includes(sso.provider_id as SsoProviderId)
    ? (sso.provider_id as SsoProviderId)
    : "other";
  let label = typeof sso.provider_label === "string" ? sso.provider_label : "";
  let logo = typeof sso.provider_logo_url === "string" ? sso.provider_logo_url : null;
  if (pid !== "other") {
    label = SSO_PROVIDER_META[pid].label;
    logo = SSO_PROVIDER_META[pid].logo;
  }
  return {
    tenantId: sso.tenant_id,
    orgName: typeof sso.org_name === "string" ? sso.org_name : sso.tenant_id,
    providerId: pid,
    providerLabel: label,
    providerLogoUrl: logo,
  };
}

/** Canonical display name + logo for each BUILT-IN provider id. `other` uses the stored label/logo. */
const SSO_PROVIDER_META: Record<Exclude<SsoProviderId, "other">, { label: string; logo: string }> = {
  entra: { label: "Microsoft Entra ID", logo: "/sso/entra.png" },
  okta: { label: "Okta", logo: "/sso/okta.png" },
  "1kosmos": { label: "1Kosmos", logo: "/sso/1kosmos.png" },
  google: { label: "Google", logo: "/sso/google.png" },
};

/**
 * The enabled factor reported by the pre-password lookup.
 *
 * The admin tab may additionally see `sso_misconfigured` (SSO enabled but no provider on the root
 * team). The user tab adds two terminal results (per-team-signin-factors tasks 5.2/5.3):
 *   - `anomaly`  — the account's active teams present an incoherent factor mix (hard stop, no login);
 *   - `no_team`  — a well-formed email (or a zero-membership account) with no domain-SSO match, or a
 *                  matched team without an SSO provider ("not part of any team").
 */
export type LoginFactor =
  | "sso"
  | "sso_misconfigured"
  | "sso_disabled"
  | "passkey"
  | "totp"
  | "password"
  | "anomaly"
  | "no_team";

/**
 * The resolved login method for an email: the enabled factor, plus the SSO team list when `sso`, and
 * the governing team when the server resolved one.
 */
export interface LoginFactorResult {
  readonly factor: LoginFactor;
  /**
   * Present when `factor === "sso"`: EVERY active team of the user that has an SSO provider (one tile
   * each; signing in via one lands the user in that team). For a domain-matched unknown email this
   * carries the single matched team's tile. Empty for all other factors.
   */
  readonly ssoTeams: readonly SsoForEmail[];
  /**
   * The governing team the credential submit must name so the server reads the per-team factor
   * (per-team-signin-factors task 5/6). Present for `totp`, `passkey`, and `sso`/`password` when the
   * server resolved a single governing team; `null` when none was resolved (e.g. `anomaly`,
   * `no_team`, or a multi-team case the user must still choose from the `ssoTeams` tiles).
   */
  readonly tenantId: string | null;
}

/**
 * Pre-password factor lookup (both login tabs): the server reports the method the USER has ENABLED
 * for their account so the login wizard can branch after "Next":
 *   - `sso`     → show the SSO tile (org + provider in `sso`); click redirects to the IdP;
 *   - `passkey` → start the passkey ceremony immediately;
 *   - `totp`    → password + OTP pane;
 *   - `password`→ password-only pane (also the safe fallback for an unknown email or any error).
 * This discloses the enabled method before authentication (a deliberate, rate-limited tradeoff).
 *
 * `surface` is the tab discriminator the server reads to pick its resolution path: `admin` resolves
 * the ROOT team's factor ONLY (so the admin tab can never return `anomaly`/`no_team`), `user` keeps
 * the existing user-tab resolution. The caller passes the current tab; omitting it defaults to the
 * user tab (and the server treats absent/other as user), matching the previous behavior.
 */
export async function getLoginFactor(
  email: string,
  surface: "admin" | "user" = "user",
  signal?: AbortSignal,
): Promise<LoginFactorResult> {
  try {
    const body = await authFetch<AuthEnvelope<{
      factor?: string;
      tenant_id?: string;
      sso_teams?: ReadonlyArray<{
        tenant_id?: string;
        org_name?: string;
        provider_id?: string;
        provider_label?: string;
        provider_logo_url?: string | null;
      }>;
    }>>("/auth/login-factors", {
      method: "POST",
      // Send the `surface` discriminator so the server runs the matching resolution path: `admin`
      // resolves the ROOT team only (never `anomaly`/`no_team`), anything else is the user tab. The
      // server defaults an absent/other value to `user`, so only `admin` must be sent to switch.
      body: surface === "admin" ? { email, surface: "admin" } : { email },
      ...(signal === undefined ? {} : { signal }),
    });
    const raw = body?.data?.factor;
    // The governing team the server resolved (per-team-signin-factors task 5/6), when present.
    const tenantId = typeof body?.data?.tenant_id === "string" ? body.data.tenant_id : null;
    if (raw === "sso") {
      const list = Array.isArray(body?.data?.sso_teams) ? body!.data!.sso_teams! : [];
      const teams: SsoForEmail[] = [];
      for (const d of list) {
        const r = resolveSso(d);
        if (r !== null) teams.push(r);
      }
      // Only report sso when at least one provider resolved; else fall back to password. The same
      // `sso` tile shape serves both the existing-account multi-team case (one tile per active SSO
      // team) and the domain-matched unknown-email case (a single tile) — no separate client branch.
      return teams.length > 0 ? { factor: "sso", ssoTeams: teams, tenantId } : { factor: "password", ssoTeams: [], tenantId: null };
    }
    if (raw === "sso_misconfigured") return { factor: "sso_misconfigured", ssoTeams: [], tenantId: null };
    if (raw === "sso_disabled") return { factor: "sso_disabled", ssoTeams: [], tenantId };
    // The two new terminal user-tab results (no SSO tiles, no governing team — a hard stop / error).
    if (raw === "anomaly") return { factor: "anomaly", ssoTeams: [], tenantId: null };
    if (raw === "no_team") return { factor: "no_team", ssoTeams: [], tenantId: null };
    const factor: LoginFactor = raw === "passkey" || raw === "totp" ? raw : "password";
    return { factor, ssoTeams: [], tenantId };
  } catch {
    return { factor: "password", ssoTeams: [], tenantId: null };
  }
}

/** Shape a raw SSO provider payload (from login-factors / sso-lookup) into {@link SsoForEmail}. */
function resolveSso(d: {
  tenant_id?: string;
  org_name?: string;
  provider_id?: string;
  provider_label?: string;
  provider_logo_url?: string | null;
}): SsoForEmail | null {
  if (typeof d.tenant_id !== "string") return null;
  const pid = (["entra", "okta", "1kosmos", "google", "other"] as const).includes(d.provider_id as SsoProviderId)
    ? (d.provider_id as SsoProviderId)
    : "other";
  let label = typeof d.provider_label === "string" ? d.provider_label : "";
  let logo = typeof d.provider_logo_url === "string" ? d.provider_logo_url : null;
  if (pid !== "other") {
    label = SSO_PROVIDER_META[pid].label;
    logo = SSO_PROVIDER_META[pid].logo;
  }
  return {
    tenantId: d.tenant_id,
    orgName: typeof d.org_name === "string" ? d.org_name : d.tenant_id,
    providerId: pid,
    providerLabel: label,
    providerLogoUrl: logo,
  };
}

/** The account page's second-factor state: what is enrolled, and which one is enabled. */
export interface SecondFactorState {
  readonly totpEnrolled: boolean;
  readonly passkeyCount: number;
  readonly active: ActiveSecondFactor;
  /** The user's selected team has an SSO provider configured. */
  readonly teamHasSso: boolean;
  /** The team enforces SSO for its members. */
  readonly teamSsoEnforced: boolean;
  /** The signed-in user is an admin of the selected team. */
  readonly isTeamAdmin: boolean;
  /** The user has enabled SSO as their own sign-in method. */
  readonly ssoLoginEnabled: boolean;
  /**
   * True when this user is a ROOT-org admin, for whom SSO as a sign-in factor is blocked (lockout
   * guard): the UI disables the SSO option and explains why. Non-admins of the root org are unaffected.
   */
  readonly ssoBlockedRootAdmin: boolean;
  /** The team's SSO provider id (entra|okta|1kosmos|google|other), when configured. */
  readonly providerId: SsoProviderId | null;
  /** The team's SSO provider display name (resolved: canonical for built-ins, stored for custom). */
  readonly providerLabel: string | null;
  /** The team's SSO provider logo URL (built-in /sso/*.png, or the custom data: URL). */
  readonly providerLogoUrl: string | null;
}

/** Read the signed-in operator's second-factor enrollment + enabled state. */
export async function getSecondFactorState(signal?: AbortSignal): Promise<SecondFactorState> {
  const body = await authFetch<AuthEnvelope<{
    totp_enrolled?: boolean;
    passkey_count?: number;
    active_second_factor?: string;
    team_has_sso?: boolean;
    team_sso_enforced?: boolean;
    is_team_admin?: boolean;
    sso_login_enabled?: boolean;
    sso_blocked_root_admin?: boolean;
    provider_id?: string | null;
    provider_label?: string | null;
    provider_logo_url?: string | null;
  }>>("/auth/second-factor", { ...(signal === undefined ? {} : { signal }) });
  const data = body?.data ?? {};
  const raw = data.active_second_factor;
  const ssoEnabled = data.sso_login_enabled === true;
  // SSO being enabled is the active option (it is mutually exclusive with totp/passkey server-side).
  const active: ActiveSecondFactor = ssoEnabled
    ? "sso"
    : raw === "totp" || raw === "passkey"
      ? raw
      : "none";
  // Resolve the provider display name + logo. For a BUILT-IN provider id the canonical name/logo win
  // (so even a config whose stored label is wrong — e.g. "Custom" — shows "1Kosmos" + the 1Kosmos
  // logo); only "other" (a custom provider) uses the admin-entered label + uploaded logo.
  const pid = (["entra", "okta", "1kosmos", "google", "other"] as const).includes(data.provider_id as SsoProviderId)
    ? (data.provider_id as SsoProviderId)
    : null;
  const storedLabel = typeof data.provider_label === "string" ? data.provider_label : null;
  const storedLogo = typeof data.provider_logo_url === "string" ? data.provider_logo_url : null;
  let providerLabel = storedLabel;
  let providerLogoUrl = storedLogo;
  if (pid !== null && pid !== "other") {
    providerLabel = SSO_PROVIDER_META[pid].label;
    providerLogoUrl = SSO_PROVIDER_META[pid].logo;
  }
  return {
    totpEnrolled: data.totp_enrolled === true,
    passkeyCount: typeof data.passkey_count === "number" ? data.passkey_count : 0,
    active,
    teamHasSso: data.team_has_sso === true,
    teamSsoEnforced: data.team_sso_enforced === true,
    isTeamAdmin: data.is_team_admin === true,
    ssoLoginEnabled: ssoEnabled,
    ssoBlockedRootAdmin: data.sso_blocked_root_admin === true,
    providerId: pid,
    providerLabel,
    providerLogoUrl,
  };
}

/**
 * Enable one of the enrolled second factors (`totp` or `passkey`), or `none`. The server refuses a
 * factor the operator has not enrolled (409), so the UI only offers "Enable" on an enrolled panel.
 */
export async function setActiveSecondFactor(factor: ActiveSecondFactor, signal?: AbortSignal): Promise<void> {
  await authFetch<AuthEnvelope<unknown>>("/auth/second-factor", {
    method: "POST",
    body: { factor },
    ...(signal === undefined ? {} : { signal }),
  });
}

/** Update the signed-in operator's display name (issue 10). Returns the saved name (or null). */
export async function updateDisplayName(displayName: string, signal?: AbortSignal): Promise<string | null> {
  const body = await authFetch<AuthEnvelope<{ display_name?: string | null }>>("/auth/profile", {
    method: "POST",
    body: { display_name: displayName },
    ...(signal === undefined ? {} : { signal }),
  });
  const d = body.data?.display_name;
  return typeof d === "string" ? d : null;
}


// -------------------------------------------------------------------------------------------------
// Credential sign-in (user + admin tabs) with the account-banned outcome (per-team-signin-factors
// task 12 / R13). Every credential path (password [+ TOTP], passkey assertion, email sign-in code)
// may be refused AFTER the credential is proven when the account/governing team is banned: the
// server answers `403 { error: "account_banned" }`. These helpers map that single outcome to a
// typed `account_banned` result so the login screen shows the account-banned message with no
// session held and no team offered; every OTHER failure (wrong password, bad code, lockout) throws
// an {@link ApiError} unchanged, so throttle/credential copy is identical for banned and unbanned
// users (the server runs the ban check only after auth succeeds — R13.5).

/** The marker the server carries in the `data.error` of its `403` account-banned envelope (R13). */
const ACCOUNT_BANNED_MARKER = "account_banned";

/**
 * Perform an `/auth/*` credential POST and detect the account-banned refusal that `authFetch` cannot
 * surface. The server answers a banned account with `403 { status:false, message, data:{ error:
 * "account_banned" } }` — the marker lives in `data.error`, which the thrown {@link ApiError} does
 * NOT carry (and a `suspended` account is also a 403, so status alone cannot distinguish them). So
 * this reads the response directly: a 403 whose body carries the marker resolves to the typed
 * `account_banned` result; every other response is handed back to {@link authFetch} semantics —
 * success resolves `ok`/`mfa_required` via `interpret`, and any other failure throws the same
 * {@link ApiError} `authFetch` would (so credential-failure/throttle copy is unchanged, R13.5).
 */
async function postCredential(
  path: string,
  body: unknown,
  interpret: (data: { mfa_required?: boolean } | null) => CredentialResult,
  signal?: AbortSignal,
): Promise<CredentialResult> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: "POST",
      headers: new Headers({ accept: "application/json", "content-type": "application/json" }),
      credentials: "same-origin",
      body: JSON.stringify(body),
      ...(signal === undefined ? {} : { signal }),
    });
  } catch (cause) {
    throw new ApiError("network", "The request could not be sent.", { cause });
  }

  const text = response.ok || response.status === 403 ? await response.text() : null;
  let parsed: unknown = null;
  if (text !== null && text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  // The banned refusal: a 403 whose envelope `data.error` carries the marker (and only that — a
  // suspended 403 has no such marker, so it falls through to the thrown ApiError below).
  if (response.status === 403 && isBannedEnvelope(parsed)) {
    return { kind: "account_banned" };
  }

  if (response.ok) {
    const data = typeof parsed === "object" && parsed !== null ? (parsed as { data?: unknown }).data : null;
    return interpret((typeof data === "object" ? (data as { mfa_required?: boolean } | null) : null) ?? null);
  }

  // Any other failure throws exactly as authFetch classifies it (status → kind), so the caller's
  // existing credential-failure / throttle handling is identical for banned and unbanned users.
  const base = { status: response.status } as const;
  const message = `Request failed with status ${response.status}.`;
  if (response.status === 503) throw new ApiError("waking", message, base);
  if (response.status === 429) throw new ApiError("request", message, { ...base, code: "locked" });
  if (response.status === 401) throw new ApiError("unauthorized", message, base);
  if (response.status === 403) throw new ApiError("forbidden", message, base);
  if (response.status === 404) throw new ApiError("not_found", message, base);
  if (response.status >= 500) throw new ApiError("server", message, base);
  throw new ApiError("request", message, base);
}

/** True when a parsed envelope carries the account-banned marker in `data.error` (R13). */
function isBannedEnvelope(parsed: unknown): boolean {
  if (typeof parsed !== "object" || parsed === null) return false;
  const data = (parsed as { data?: unknown }).data;
  if (typeof data !== "object" || data === null) return false;
  return (data as { error?: unknown }).error === ACCOUNT_BANNED_MARKER;
}

/**
 * The result of a credential sign-in attempt that the server accepted at the credential layer:
 *   - `ok`              — credentials proven and a session was issued (the SPA reads the session);
 *   - `mfa_required`    — the password was accepted and the server now wants the 6-digit TOTP code
 *                         (not a failure; the user tab reveals the OTP box without counting it);
 *   - `account_banned`  — credentials proven but the account/governing team is banned: no session,
 *                         show the account-banned message.
 * Any genuine credential failure (wrong password/OTP/code, lockout) is thrown as an {@link ApiError}
 * rather than returned here, so the caller's existing failure handling is unchanged.
 */
export type CredentialResult =
  | { readonly kind: "ok" }
  | { readonly kind: "mfa_required" }
  | { readonly kind: "account_banned" };

/**
 * Sign in with a password (user + admin tabs). `tenantId` is the governing team resolved at the
 * email step (per-team-signin-factors task 6): when present it is threaded into the submit so the
 * server reads the per-team factor and sets the issued session's selected team. `code` carries the
 * TOTP when the factor is `totp` (or after an `mfa_required` challenge). Maps the `403 account_banned`
 * refusal to the typed `account_banned` result; any other failure throws an {@link ApiError}.
 */
export async function loginWithPassword(
  input: { readonly email: string; readonly password: string; readonly code?: string; readonly tenantId?: string | null },
  signal?: AbortSignal,
): Promise<CredentialResult> {
  return postCredential(
    "/auth/login",
    {
      email: input.email,
      password: input.password,
      ...(input.code !== undefined && input.code.length > 0 ? { code: input.code } : {}),
      ...(input.tenantId !== undefined && input.tenantId !== null ? { tenantId: input.tenantId } : {}),
    },
    (data) => (data?.mfa_required === true ? { kind: "mfa_required" } : { kind: "ok" }),
    signal,
  );
}

/**
 * Complete an email sign-in by verifying the single-use code (`POST /auth/verify {email, code}`).
 * Maps the `403 account_banned` refusal to the typed `account_banned` result; any other failure
 * (wrong/expired code) throws an {@link ApiError}.
 */
export async function verifyEmailCode(
  email: string,
  code: string,
  signal?: AbortSignal,
): Promise<CredentialResult> {
  return postCredential("/auth/verify", { email, code }, () => ({ kind: "ok" }), signal);
}

/**
 * Sign in with a passkey (passwordless) and surface the account-banned outcome typed. Runs the same
 * browser ceremony as {@link loginWithPasskey} — begin options via {@link authFetch}, the native
 * `startAuthentication` — but posts the assertion through {@link postCredential} so the server's
 * `403 account_banned` on the verify step maps to `account_banned` instead of throwing. A
 * cancelled/failed native prompt still throws (the caller shows a friendly "cancelled"), as does any
 * other server rejection of the assertion.
 */
export async function loginWithPasskeyResult(signal?: AbortSignal): Promise<CredentialResult> {
  const begin = await authFetch<{ data?: { handle?: string; options?: PublicKeyCredentialRequestOptionsJSON } }>(
    "/auth/webauthn/authenticate/options",
    { method: "POST", ...(signal === undefined ? {} : { signal }) },
  );
  const handle = begin?.data?.handle;
  const options = begin?.data?.options;
  if (typeof handle !== "string" || options === undefined) {
    throw new ApiError("malformed", "The server did not return a sign-in challenge.");
  }
  const response = await startAuthentication({ optionsJSON: options });
  return postCredential("/auth/webauthn/authenticate/verify", { handle, response }, () => ({ kind: "ok" }), signal);
}

// -------------------------------------------------------------------------------------------------
// SSO callback query outcomes (per-team-signin-factors task 7 redirects → task 12/13 messages). The
// SSO callback mints no session for a banned or already-existing account; it redirects back to the
// login page carrying `?sso=banned` or `?sso=account_exists` so the SPA shows the matching terminal
// message. These helpers turn the raw query value into a typed outcome the login screen branches on.

/** A typed SSO callback outcome the login page shows a terminal message for. */
export type SsoOutcome =
  | "banned" //         the account/team is banned: show the account-banned message, no session.
  | "account_exists" // an account already exists for this email with no identity for this IdP.
  | "pending" //        self-provisioned, awaiting approval (the Access Requested landing).
  | "none"; //          no recognised SSO outcome in the query.

/**
 * Classify an `sso` query value from the SSO callback redirect into a typed {@link SsoOutcome}. Pass
 * the raw value (e.g. `new URLSearchParams(location.search).get("sso")`); an absent/unknown value
 * maps to `none`. `banned` → account-banned message (R13.1); `account_exists` → "an account already
 * exists for this email, contact your team admin"; `pending` → the Access Requested landing.
 */
export function ssoOutcomeFromQuery(value: string | null | undefined): SsoOutcome {
  switch (value) {
    case "banned":
      return "banned";
    case "account_exists":
      return "account_exists";
    case "pending":
      return "pending";
    default:
      return "none";
  }
}
